import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { filter, map, mergeMap, switchMap } from 'rxjs/operators'
import { ActivatedRoute, Router } from '@angular/router'
import { RouterQueryParams } from '../../../shared/router-query-params.service'
import { Facility } from '../../facilities/facility'
import { FacilityService } from '../../facilities/facility.service'
import { PrincipalService } from '@elderbyte/ngx-auth'
import { SettingsStorageService } from '../../settings/settings-storage.service'

@Injectable({
  providedIn: 'root',
})
export class FacilityContextService {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _activeFacility = new BehaviorSubject<Facility>(null)
  private readonly _availableFacilities$ = new BehaviorSubject<Facility[]>([])
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)
  private facilityService = inject(FacilityService)
  private routerQueryParams = inject(RouterQueryParams)
  private principalService = inject(PrincipalService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor(private settingsService: SettingsStorageService) {
    // Todo set based on user permissions
    this.principalService.isAvailableObservable
      .pipe(
        filter((isAvailable) => isAvailable),
        switchMap(() => this.facilityService.findAll())
      )
      .subscribe((facilities) => {
        this._availableFacilities$.next(facilities)

        // only if we don't yet have an active facility we want to set it
        if (!this.facility) {
          const storedFacility = this.determineBestFacilityFromStorage(facilities)
          const fallbackFacility = facilities[0]
          this.setFacility(storedFacility ? storedFacility : fallbackFacility, false)
        }
      })

    // Check for facility query param on all router levels
    combineLatest([
      this.principalService.isAvailableObservable,
      this.routerQueryParams.params.pipe(map((params) => params['facility'])),
    ])
      .pipe(
        filter(([principalAvailable, facilityParam]) => principalAvailable && facilityParam),
        mergeMap(([_, facilityParam]) => this.facilityService.findById(facilityParam))
      )
      .subscribe({
        next: (facility) => {
          if (this.facility?.id !== facility.id) {
            this.setFacility(facility, false)
          }
        },
        error: (err) => this.log.error('Failed to load facility', err),
      })
  }

  /***************************************************************************
   *                                                                         *
   * Company Getters / Setters                                               *
   *                                                                         *
   **************************************************************************/

  public get availableFacilities$(): Observable<Facility[]> {
    return this._availableFacilities$.asObservable()
  }

  public get facility$(): Observable<Facility> {
    return this._activeFacility.pipe(filter((facility) => !!facility))
  }

  public get facility(): Facility | null {
    return this._activeFacility.getValue()
  }

  public setFacility(facility: Facility, updateQueryParam = true): void {
    if (!this.facilityEquals(this.facility, facility)) {
      this._activeFacility.next(facility)
      this.settingsService.setCurrentFacility(facility.id)
      if (updateQueryParam) {
        this.updateFacilityQueryParam(facility.id)
      }
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private determineBestFacilityFromStorage(facilities: Facility[]): Facility | null {
    const currentFacilityId = this.settingsService.getCurrentFacility()
    return facilities.find((facility) => facility.id === currentFacilityId)
  }

  private facilityEquals(before: Facility, after: Facility): boolean {
    if (before && after) {
      return before.id === after.id
    } else {
      return before === after
    }
  }

  private updateFacilityQueryParam(facility: string): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { facility: facility },
      replaceUrl: true, // if true, won't add a new browser history entry
      queryParamsHandling: 'merge',
    })
  }
}
